import { normalizeName } from '@obraplan/core/domain/shared/naming';
import {
  groundBeamFreeLength,
  pointInPolygon,
  type GroundBeam,
  type PileCap,
} from '@obraplan/core/domain/takeoff/foundation';
import { isValidOutline, type Point } from '@obraplan/core/domain/takeoff/polygon';
import { metros } from './formato';
import type { Pixel } from './plan-tracer';

/**
 * As contas do bloco e do baldrame na prancha, sem tela (Fase 27, etapa 2 — D3, D6, F14).
 *
 * Moram à parte do desenho para que o que a prancha diz — o próximo nome, o encaixe do clique
 * no centro do bloco, o trecho do baldrame que passa dentro dele — seja número que se testa
 * sem montar a prancha.
 */

/**
 * As cores do bloco e do baldrame, na prancha e na legenda. Não seguem o tema, pelo mesmo
 * motivo das do resto do traçado: a prancha é papel branco. O baldrame é violeta como a viga
 * da estrutura — é a mesma família de peça, enterrada.
 */
export const COR_DO_BLOCO = '#0f766e';
export const COR_DO_BALDRAME = '#7c3aed';

/**
 * O próximo nome livre, na sequência do último desenhado: "B14" → "B15", "VB3" → "VB4".
 *
 * Só o primeiro número do nome anda; o resto — a letra, o sufixo — é do projeto de fundação e
 * fica como está. Sem número no último nome, ou sem peça nenhuma, a sugestão começa em 1.
 * Pula o que já existe, porque a sugestão recusada seria pior que nenhuma.
 */
export function proximoNomeDaPeca(
  pecas: readonly { readonly name: string }[],
  prefixoPadrao: string,
): string {
  const usados = new Set(pecas.map((peca) => normalizeName(peca.name)));
  const ultimo = pecas[pecas.length - 1]?.name.trim() ?? '';
  const achado = /^(\D*)(\d+)(.*)$/s.exec(ultimo);
  const prefixo = achado?.[1] ?? prefixoPadrao;
  const sufixo = achado?.[3] ?? '';
  let numero = achado === null ? 1 : Number(achado[2]) + 1;
  for (let tentativas = 0; tentativas < 10_000; tentativas += 1, numero += 1) {
    const candidato = `${prefixo}${String(numero)}${sufixo}`;
    if (!usados.has(normalizeName(candidato))) return candidato;
  }
  return `${prefixo}${String(pecas.length + 1)}${sufixo}`;
}

export const proximoNomeDeBloco = (blocos: readonly { readonly name: string }[]): string =>
  proximoNomeDaPeca(blocos, 'B');

export const proximoNomeDeBaldrame = (baldrames: readonly { readonly name: string }[]): string =>
  proximoNomeDaPeca(baldrames, 'VB');

// ── O bloco ─────────────────────────────────────────────────────────────────────

/**
 * Os quatro cantos do retângulo que os dois cantos opostos definem — o bloco "por dois
 * cantos", como o shaft. Genérico para servir ao pixel da prancha e ao metro do desenho.
 */
export function retanguloPorDoisCantos<T extends Point>(a: T, b: T): readonly Point[] {
  return [
    { x: a.x, y: a.y },
    { x: b.x, y: a.y },
    { x: b.x, y: b.y },
    { x: a.x, y: b.y },
  ];
}

/**
 * O centro do contorno — o centro de área, que é onde o eixo do baldrame se encontra.
 *
 * Num bloco de três estacas, o triângulo, é o baricentro: o ponto que o projeto de fundação
 * cota. Contorno sem área cai na média dos vértices.
 */
export function centroDoContorno(outline: readonly Point[]): Point {
  let dobroDaArea = 0;
  let somaX = 0;
  let somaY = 0;
  outline.forEach((p, indice) => {
    const q = outline[(indice + 1) % outline.length]!;
    const cruzado = p.x * q.y - q.x * p.y;
    dobroDaArea += cruzado;
    somaX += (p.x + q.x) * cruzado;
    somaY += (p.y + q.y) * cruzado;
  });
  if (Math.abs(dobroDaArea) < 1e-9) {
    const total = Math.max(outline.length, 1);
    return {
      x: outline.reduce((soma, p) => soma + p.x, 0) / total,
      y: outline.reduce((soma, p) => soma + p.y, 0) / total,
    };
  }
  return { x: somaX / (3 * dobroDaArea), y: somaY / (3 * dobroDaArea) };
}

type BlocoNoDesenho = Pick<PileCap, 'outline' | 'deletedAt'>;

/** O bloco em cujo contorno o ponto cai — o último desenhado, se houver sobreposição. */
export function blocoSobOPonto<T extends BlocoNoDesenho>(
  ponto: Point,
  blocos: readonly T[],
): T | undefined {
  return [...blocos]
    .reverse()
    .find(
      (bloco) =>
        bloco.deletedAt === undefined &&
        isValidOutline(bloco.outline) &&
        pointInPolygon(ponto, bloco.outline),
    );
}

/**
 * O clique do baldrame, encaixado no centro do bloco quando cai dentro de um (D6).
 *
 * É o que faz o eixo ir "de eixo a eixo dos blocos" sem a pessoa mirar no centro de cada um:
 * o clique em qualquer lugar do bloco vale o centro dele. Fora de bloco o ponto fica onde foi
 * clicado — a ponta apoiada direto na estaca (F14).
 */
export function encaixarNoBloco(ponto: Point, blocos: readonly BlocoNoDesenho[]): Point {
  const bloco = blocoSobOPonto(ponto, blocos);
  return bloco === undefined ? ponto : centroDoContorno(bloco.outline);
}

// ── O baldrame ──────────────────────────────────────────────────────────────────

/** "20 × 40" — a seção como a obra fala: largura e altura em centímetros. */
export function secaoDoBaldrame(beam: Pick<GroundBeam, 'width' | 'height'>): string {
  return `${String(Math.round(beam.width * 100))} × ${String(Math.round(beam.height * 100))}`;
}

/** O eixo de ponta a ponta, sem descontar bloco nenhum. Metros, duas casas. */
export function comprimentoDoEixo(beam: Pick<GroundBeam, 'from' | 'to'>): number {
  return Math.round(Math.hypot(beam.to.x - beam.from.x, beam.to.y - beam.from.y) * 100) / 100;
}

/**
 * Os trechos do segmento que passam dentro de um polígono, em fração do segmento (0 a 1).
 *
 * É a mesma conta de `groundBeamFreeLength`, que a faz por dentro e não devolve os trechos:
 * a prancha precisa deles para desenhar mais claro o que não conta. O teste confere que as
 * duas fecham no mesmo número.
 */
function trechosDentro(from: Point, to: Point, outline: readonly Point[]): [number, number][] {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const cortes = [0, 1];
  for (let i = 0; i < outline.length; i += 1) {
    const a = outline[i]!;
    const b = outline[(i + 1) % outline.length]!;
    const ex = b.x - a.x;
    const ey = b.y - a.y;
    const denominador = dx * ey - dy * ex;
    if (Math.abs(denominador) < 1e-12) continue;
    const t = ((a.x - from.x) * ey - (a.y - from.y) * ex) / denominador;
    const u = ((a.x - from.x) * dy - (a.y - from.y) * dx) / denominador;
    if (t > 0 && t < 1 && u >= 0 && u <= 1) cortes.push(t);
  }
  cortes.sort((p, q) => p - q);
  const dentro: [number, number][] = [];
  for (let i = 0; i < cortes.length - 1; i += 1) {
    const [inicio, fim] = [cortes[i]!, cortes[i + 1]!];
    if (fim - inicio < 1e-9) continue;
    const meio = (inicio + fim) / 2;
    if (pointInPolygon({ x: from.x + dx * meio, y: from.y + dy * meio }, outline)) {
      dentro.push([inicio, fim]);
    }
  }
  return dentro;
}

/** Um pedaço do eixo, em fração dele, e se passa dentro de algum bloco (RN-368). */
export interface TrechoDoBaldrame {
  readonly de: number;
  readonly ate: number;
  readonly dentro: boolean;
}

/**
 * O eixo repartido em trechos, do começo ao fim: os que passam dentro dos blocos e os livres.
 *
 * Os trechos de blocos que se sobrepõem são unidos antes — dois blocos encostados são um só
 * pedaço coberto. Sem bloco, o eixo inteiro é um trecho livre.
 */
export function trechosDoBaldrame(
  beam: Pick<GroundBeam, 'from' | 'to'>,
  caps: readonly BlocoNoDesenho[],
): readonly TrechoDoBaldrame[] {
  const cobertos = caps
    .filter((cap) => cap.deletedAt === undefined && isValidOutline(cap.outline))
    .flatMap((cap) => trechosDentro(beam.from, beam.to, cap.outline))
    .sort((p, q) => p[0] - q[0]);

  const unidos: [number, number][] = [];
  for (const [inicio, fim] of cobertos) {
    const ultimo = unidos[unidos.length - 1];
    if (ultimo !== undefined && inicio <= ultimo[1] + 1e-9) ultimo[1] = Math.max(ultimo[1], fim);
    else unidos.push([inicio, fim]);
  }

  const trechos: TrechoDoBaldrame[] = [];
  let cursor = 0;
  for (const [inicio, fim] of unidos) {
    if (inicio > cursor + 1e-9) trechos.push({ de: cursor, ate: inicio, dentro: false });
    trechos.push({ de: inicio, ate: fim, dentro: true });
    cursor = fim;
  }
  if (cursor < 1 - 1e-9) trechos.push({ de: cursor, ate: 1, dentro: false });
  return trechos;
}

/** O rótulo na prancha: o nome e o comprimento livre — o que conta depois do desconto. */
export function rotuloDoBaldrame(
  beam: Pick<GroundBeam, 'name' | 'from' | 'to'>,
  caps: readonly BlocoNoDesenho[],
): string {
  return `${beam.name} · ${metros(groundBeamFreeLength(beam, caps))} m`;
}

// ── O desenho ───────────────────────────────────────────────────────────────────

export const pontosDe = (vertices: readonly Pixel[]): string =>
  vertices.map((vertice) => `${String(vertice.x)},${String(vertice.y)}`).join(' ');

/** Os quatro cantos da faixa de um eixo, na largura dada — a seção vista de cima. */
export function faixaDoEixo(de: Pixel, ate: Pixel, largura: number): readonly Pixel[] {
  const dx = ate.x - de.x;
  const dy = ate.y - de.y;
  const comprimento = Math.hypot(dx, dy) || 1;
  const nx = ((-dy / comprimento) * largura) / 2;
  const ny = ((dx / comprimento) * largura) / 2;
  return [
    { x: de.x + nx, y: de.y + ny },
    { x: ate.x + nx, y: ate.y + ny },
    { x: ate.x - nx, y: ate.y - ny },
    { x: de.x - nx, y: de.y - ny },
  ];
}
